import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryColorTableCell from '../../../components/PrimaryColorTableCell/PrimaryColorTableCell';
import Histogram from '../../../model/API/Histogram/Histogram';
import HistogramTableRow from '../model/HistogramTableRow';

const QFlowTable: React.FC<QFlowTableProps> = ({
  qFlow,
  loading,
  error,
  unit,
}: QFlowTableProps) => {
  const { t } = useTranslation();

  const tableItems = useMemo<HistogramTableRow[]>(() => {
    if (!qFlow) {
      return [];
    }

    return Array.from({ length: qFlow.nbins - 1 }, (_: unknown, index: number) => {
      const value = qFlow.values[index];
      const rangeLowerBound = index > 0 ? `Q${index}` : '0';
      const rangeUpperBound = index + 1 < qFlow.nbins - 1 ? `Q${index + 1}` : 'Infinity';
      const percent = qFlow.total > 0 ? +((value / qFlow.total) * 100).toFixed(2) : 0;

      return {
        range: `${rangeLowerBound} - ${rangeUpperBound}`,
        value: +value.toFixed(2),
        percent,
      };
    });
  }, [qFlow]);

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <PrimaryColorTableCell align="center">{t('flow')}</PrimaryColorTableCell>
            <PrimaryColorTableCell align="center">{unit}</PrimaryColorTableCell>
            <PrimaryColorTableCell align="center">{t('percent')}</PrimaryColorTableCell>
          </TableRow>
        </TableHead>
        {!loading && (
          <TableBody>
            {tableItems.length > 0 ? (
              tableItems.map((item: HistogramTableRow) => (
                <TableRow hover key={item.range}>
                  <TableCell align="center">
                    <Box fontWeight="fontWeightMedium">{item.range}</Box>
                  </TableCell>
                  <TableCell align="center">{`${item.value}${unit ? ` ${unit}` : ''}`}</TableCell>
                  <TableCell align="center">{item.percent} %</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={3} align="center">
                  <Typography variant="subtitle1" color="gray" paddingTop={8} paddingBottom={8}>
                    {error || t('no_data_found')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
      {loading && <LinearProgress color="primary" />}
    </TableContainer>
  );
};

interface QFlowTableProps {
  qFlow: Histogram | null;
  loading: boolean;
  error: string | null;
  unit: string;
}

export default QFlowTable;
